import { DataTable } from "@design";
import { useQuery } from "@tanstack/react-query";
import { Blocks } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { type ExtensionSummary, listExtensionsOptions } from "@/api";
import { EmptyState } from "@/components/build/EmptyState";
import { PageSection } from "@/components/section/SectionBlocks";
import { Text } from "@/components/Text";
import { AdminPage } from "../AdminPage";
import { useAdminCanEdit } from "../useAdminCanEdit";
import { AdminConnectorsSection } from "./AdminConnectorsSection";
import { ConnectorActivitySection } from "./ConnectorActivitySection";
import { ConnectorPolicySection } from "./ConnectorPolicySection";
import { type IntegrationSection, IntegrationsTabs } from "./IntegrationsTabs";
import { useIntegrationColumns } from "./integrationColumns";
import { MicrosoftPermissionsSection } from "./MicrosoftPermissionsSection";
import { isBuiltIn } from "./setupStatus";
import { WriteActionsPolicySection } from "./WriteActionsPolicySection";

/**
 * Admin > Integrations, in three sections with a URL each: the catalog (/admin/integrations), what AnthmAI may do
 * (/admin/integrations/permissions: write actions, Microsoft 365 permissions) and the connectors people add
 * (/admin/integrations/connectors). See docs/design/web/WEB_EXTENSIONS_PLAN.md §5.
 */
export function IntegrationsPage({ section = "catalog" }: Readonly<{ section?: IntegrationSection }>) {
  const { t } = useTranslation("adminIntegrations");
  const canEdit = useAdminCanEdit();
  return (
    <AdminPage title={t("title")} subtitle={t("subtitle")}>
      <IntegrationsTabs active={section} />
      {section === "catalog" && <Catalog />}
      {section === "permissions" && (
        <>
          <WriteActionsPolicySection canEdit={canEdit} />
          <MicrosoftPermissionsSection />
        </>
      )}
      {section === "connectors" && (
        <>
          <ConnectorPolicySection canEdit={canEdit} />
          <AdminConnectorsSection canEdit={canEdit} />
          <ConnectorActivitySection />
        </>
      )}
    </AdminPage>
  );
}

/** Every integration AnthmAI can connect to and what each still needs, then the built-ins. */
function Catalog() {
  const { t } = useTranslation("adminIntegrations");
  const navigate = useNavigate();
  const extensions = useQuery({ ...listExtensionsOptions(), select: (r) => r.data });
  const columns = useIntegrationColumns();
  const all = extensions.data ?? [];
  const open = (extension: ExtensionSummary) => navigate(`/admin/integrations/${extension.uuid}`);

  if (extensions.isError) {
    return (
      <Text tone="error" role="alert">
        {t("loadFailed")}
      </Text>
    );
  }
  const table = (label: string, rows: ExtensionSummary[]) => (
    <DataTable
      aria-label={label}
      columns={columns}
      rows={rows}
      rowKey={(extension) => extension.uuid}
      loading={extensions.isPending}
      onRowClick={open}
      empty={<EmptyState icon={<Blocks size={32} />} title={t("empty")} body={t("emptyBody")} />}
    />
  );
  return (
    <>
      <PageSection title={t("sections.integrations")} description={t("sections.integrationsBody")}>
        {table(
          t("tableLabel"),
          all.filter((e) => !isBuiltIn(e)),
        )}
      </PageSection>
      <PageSection title={t("sections.builtIn")} description={t("sections.builtInBody")}>
        {table(t("builtInTableLabel"), all.filter(isBuiltIn))}
      </PageSection>
    </>
  );
}
